/* journeys.js — canonical Transaction Journey templates.
 *
 * Drives the horizontal journey strip on the Deal Room. Templates
 * are selected dynamically from `(tenure × funding × buildKind)` so
 * the steps reflect the *real* operational shape of the deal:
 *
 *   • Leasehold + Mortgage   → 13 steps  (canonical reference)
 *   • Freehold  + Mortgage   →  9 steps  (no management pack)
 *   • Leasehold + Cash       →  9 steps  (no mortgage survey/offer)
 *   • Freehold  + Cash       →  6 steps  (the simplest path)
 *
 * Every step maps to an `eventKey` from operational_events.CATALOGUE
 * so completion is derived from the deal activity log — no parallel
 * state.
 */

export const FULL_STEPS = {
  offer_accepted:           { key: "offer_accepted",            label: "Offer\naccepted",            eventKey: "memorandum_issued" },
  aml_complete:             { key: "aml_complete",              label: "AML\ncomplete",              eventKey: "aml_verified" },
  memorandum_issued:        { key: "memorandum_issued",         label: "Memorandum\nissued",         eventKey: "memorandum_issued" },
  mortgage_survey_booked:   { key: "mortgage_survey_booked",    label: "Mortgage\nsurvey booked",    eventKey: "mortgage_valuation_booked",  fundingOnly: "mortgage" },
  contract_pack_sent:       { key: "contract_pack_sent",        label: "Contract\npack sent",        eventKey: "draft_contracts_received" },
  contract_pack_received:   { key: "contract_pack_received",    label: "Contract\npack received",    eventKey: "draft_contracts_received" },
  management_pack_ordered:  { key: "management_pack_ordered",   label: "Management\npack ordered",   eventKey: "leasehold_pack_requested",   tenureOnly: "leasehold" },
  management_pack_received: { key: "management_pack_received",  label: "Management\npack received",  eventKey: "management_pack_received",   tenureOnly: "leasehold" },
  searches_returned:        { key: "searches_returned",         label: "Searches\nreturned",         eventKey: "searches_returned" },
  mortgage_offer_received:  { key: "mortgage_offer_received",   label: "Mortgage\noffer received",   eventKey: "mortgage_offer_received",    fundingOnly: "mortgage" },
  exchange_agreed:          { key: "exchange_agreed",           label: "Exchange\nagreed",           eventKey: "exchange_agreed" },
  completion:               { key: "completion",                label: "Completion",                 eventKey: "completion_funds_transferred" },
};

/* Canonical ordering — leasehold + mortgage path (reference design).
 * Other deal shapes filter from this list via the `tenureOnly` /
 * `fundingOnly` markers above. The order itself is unchanged. */
const CANONICAL_ORDER = [
  "offer_accepted",
  "aml_complete",
  "memorandum_issued",
  "mortgage_survey_booked",
  "contract_pack_sent",
  "contract_pack_received",
  "management_pack_ordered",
  "management_pack_received",
  "searches_returned",
  "mortgage_offer_received",
  "exchange_agreed",
  "completion",
];

/* Build the active journey steps for a given deal shape. */
export function buildJourney({ tenure = "leasehold", funding = "mortgage" } = {}) {
  const t = (tenure || "").toLowerCase().includes("lease") ? "leasehold" : "freehold";
  const f = (funding || "").toLowerCase().includes("cash") ? "cash" : "mortgage";
  return CANONICAL_ORDER
    .map((k) => FULL_STEPS[k])
    .filter((s) => {
      if (s.tenureOnly && s.tenureOnly !== t) return false;
      if (s.fundingOnly && s.fundingOnly !== f) return false;
      return true;
    });
}

/* Derive per-step status from the deal activity log. */
export function deriveJourneyStatuses(steps, activities, deal) {
  const completed = new Set();
  for (const a of activities || []) {
    const k = a?.meta?.event_key || a?.event_key;
    if (k) completed.add(k);
  }
  let firstIncomplete = -1;
  return steps.map((s, idx) => {
    const isDone = completed.has(s.eventKey);
    if (!isDone && firstIncomplete === -1) firstIncomplete = idx;
    return {
      ...s,
      status: isDone ? "completed" : "pending",
      completedAt: null,
    };
  }).map((s, idx) => ({
    ...s,
    status: s.status === "completed"
      ? "completed"
      : (idx === firstIncomplete ? "in_progress" : "pending"),
  }));
}

/* Pretty date for the step caption. */
export function fmtJourneyDate(iso) {
  if (!iso) return "Pending";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Pending";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
